/**
 * The nine workspaces (D19 phase 21, from the v6 kit's NAVIGATION.md and the owner's reference boards).
 *
 * The registry keeps every screen's id, path and section as they were — addresses, tests and the field apps depend
 * on them. This file only says where each screen sits in the menu: its workspace, its group, its order. A screen that
 * is not built still keeps a place (owner rule, 2026-09-28): the sidebar gathers those into each workspace's folded
 * "Planned" group, so the built groups stay as short as the kit asks.
 *
 * Every screen the sidebar can show is in exactly one workspace; a test fails otherwise. `links` are second entries
 * that point at a screen whose home is elsewhere (the kit's "Rider payouts (link)").
 */
import { MENU_LABEL, MOVED, RECORD_HOME } from './v7';

export type WorkspaceId = 'home' | 'crm' | 'automation' | 'operations' | 'b2b' | 'money' | 'supply' | 'people' | 'insights' | 'admin';

export interface WorkspaceGroup {
  /** The small heading; '' for items that sit above every heading. */
  readonly label: string;
  readonly labelHi?: string;
  readonly screens: readonly string[];
  readonly links?: readonly string[];
}

export interface Workspace {
  readonly id: WorkspaceId;
  readonly label: string;
  readonly labelHi: string;
  /** lucide icon name, mapped in the shell. */
  readonly icon: string;
  readonly groups: readonly WorkspaceGroup[];
}

export const WORKSPACES: readonly Workspace[] = [
  // v7 Part E (CORE-1): at most 12 items each, Overview first and Reports last; nothing planned (Admin › Roadmap lists
  // it); B2B off the rail until its release ships a working screen. A screen that a later release replaces stays here,
  // cleaned, until then; merged screens are tabs of their hub (v7.ts).
  { id: 'home', label: 'Home', labelHi: 'होम', icon: 'house', groups: [
    { label: '', screens: ['home.my-day', 'overview.attention', 'admin.approvals', 'admin.notifications', 'home.erp-changes'] },
  ] },
  { id: 'crm', label: 'CRM', labelHi: 'ग्राहक', icon: 'contact', groups: [
    // The owner, 2026-10-18 (CRM brief §2): The day · Customers · Grow · Output, with the screens that are built.
    { label: 'The day', labelHi: 'आज का दिन', screens: ['crm.overview', 'crm.map', 'crm.delivery-changes'] },
    // Start a customer and Onboarding are tabs of Customer 360; the service desk and requests are tabs of Messages.
    { label: 'Customers', labelHi: 'ग्राहक', screens: ['customers.master', 'crm.performance', 'crm.money', 'crm.payments', 'crm.bottles', 'crm.messages'] },
    { label: 'Grow', labelHi: 'बढ़त', screens: ['crm.leads'] },
    // The owner, 2026-10-26: ghee is a section of its own (like Grow and its Inquiries), each part its own screen; the later parts (Recurring, Reminders, Billing, Courier, Automation) are added as they are built.
    { label: 'Ghee', labelHi: 'घी', screens: ['crm.ghee-overview', 'crm.ghee-today', 'crm.ghee-upcoming', 'crm.ghee-recurring', 'crm.ghee-reminders', 'crm.ghee-customers', 'crm.ghee-billing', 'crm.ghee-courier', 'crm.ghee-variants'] },
    { label: 'Output', labelHi: 'रिपोर्ट', screens: ['crm.reports'] },
  ] },
  { id: 'automation', label: 'Automation', labelHi: 'ऑटोमेशन', icon: 'message-square', groups: [
    { label: '', screens: ['automation.overview', 'automation.whatsapp', 'automation.reminders', 'automation.broadcast', 'automation.settings', 'automation.instructions', 'automation.learnings', 'automation.outcomes'] },
  ] },
  { id: 'operations', label: 'Operations', labelHi: 'संचालन', icon: 'bike', groups: [
    { label: '', screens: ['ops.overview'] },
    { label: 'Plan', labelHi: 'योजना', screens: ['delivery.plan', 'delivery.orders', 'ops.packing-orders'] },
    { label: 'Deliver', labelHi: 'डिलीवरी', screens: ['ops.rounds-loading', 'ops.live', 'ops.issues', 'ops.records', 'ops.day-close'] },
    { label: 'Setup', labelHi: 'सेटअप', screens: ['ops.routes-riders', 'ops.bottles'] },
    { label: '', screens: ['ops.reports'] },
  ] },
  // The owner, 2026-10-13 (migration 221): B2B is on the rail with its working screens; standing orders stay planned.
  { id: 'b2b', label: 'B2B', labelHi: 'बी2बी', icon: 'building', groups: [
    { label: '', screens: ['b2b.accounts', 'b2b.dispatch'] },
  ] },
  { id: 'supply', label: 'Supply', labelHi: 'आपूर्ति', icon: 'factory', groups: [
    { label: '', screens: ['supply.overview'] },
    { label: 'Farm', labelHi: 'फार्म', screens: ['supply.farm-overview', 'supply.farms-loci', 'supply.collections', 'supply.barrels'] },
    { label: 'Plant', labelHi: 'प्लांट', screens: ['supply.plant-overview', 'supply.receiving', 'supply.filling', 'supply.cold-room'] },
    { label: '', screens: ['plant.inventory', 'supply.trace', 'supply.notices', 'supply.reports'] },
  ] },
  // Finance Phase 0 (2026-11-01): Money became Finance. The id stays 'money' (screen ids, folders and stored codes depend
  // on it); the label, the Hindi label and the addresses (/finance/..., old ones redirect: PATH_MOVED) changed. Billing,
  // Cash, Books follow the Finance spec section 5; the sections not built yet are on the Roadmap, never here.
  { id: 'money', label: 'Finance', labelHi: 'वित्त', icon: 'landmark', groups: [
    { label: 'Billing', labelHi: 'बिलिंग', screens: ['money.milk-bills', 'finance.invoices', 'finance.notes', 'finance.late-charge', 'finance.templates', 'money.prices'] },
    { label: 'Cash', labelHi: 'नकद', screens: ['money.banks', 'money.ghee-bills', 'workforce.incentives', 'farm.payments'] },
    { label: 'Books', labelHi: 'खाते', screens: ['finance.transactions', 'finance.ledger-check', 'finance.tax-rates', 'money.reports'] },
  ] },
  { id: 'people', label: 'People', labelHi: 'लोग', icon: 'users', groups: [
    { label: '', screens: ['people.list', 'people.attendance-shifts', 'people.reports'] },
  ] },
  { id: 'insights', label: 'Insights', labelHi: 'जानकारी', icon: 'chart-line', groups: [
    { label: '', screens: ['insights.director', 'insights.map', 'insights.reports'] },
  ] },
  { id: 'admin', label: 'Admin', labelHi: 'एडमिन', icon: 'shield', groups: [
    { label: '', screens: ['admin.control-centre'] },
    { label: 'Data', labelHi: 'डेटा', screens: ['admin.master-data', 'admin.qr', 'admin.import-centre', 'workforce.assets', 'admin.uat-reset'] },
    { label: '', screens: ['admin.integrations'] },
    { label: 'Access', labelHi: 'पहुँच', screens: ['admin.users-roles', 'admin.audit-logs'] },
    { label: '', screens: ['admin.roadmap'] },
  ] },
];

/** The workspace a screen lives in (its home, not a link). */
export function workspaceOf(screenId: string): Workspace | undefined {
  // A record page belongs to its list's workspace; a moved screen to where it lives now (v7.ts).
  const id = RECORD_HOME[screenId] ?? MOVED[screenId]?.to ?? screenId;
  return WORKSPACES.find((w) => w.groups.some((g) => g.screens.includes(id)));
}

/** The menu item a screen is reached from: itself, its record list, or the hub it moved into. */
export function menuScreenOf(screenId: string): string {
  return RECORD_HOME[screenId] ?? MOVED[screenId]?.to ?? screenId;
}

/** The name people read for a screen (Part E): its menu label where v7 renames it, else the registry label. */
export function menuLabel(screen: { id: string; label: string }): string {
  return MENU_LABEL[screen.id] ?? screen.label;
}

/** Landing screen by role (kit NAVIGATION.md): the first rule whose role the person holds wins. */
export const LANDING_BY_ROLE: readonly { readonly roles: readonly string[]; readonly screen: string }[] = [
  // v7 Part E: owner and director on Home › My day until the Director dashboard v1 (CORE-11); a landing screen that does
  // not exist yet falls back to My day.
  { roles: ['erp_admin', 'owner', 'director', 'management'], screen: 'home.my-day' },
  { roles: ['delivery_supervisor'], screen: 'ops.live' },
  { roles: ['plant_manager', 'plant_supervisor'], screen: 'supply.plant-overview' },
  { roles: ['farm_manager'], screen: 'supply.farm-overview' },
  { roles: ['hr_manager'], screen: 'people.list' },
];
export const LANDING_DEFAULT = 'home.my-day';
